import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { Provider } from '@atlaskit/collab-provider/provider';
import { cssMap } from '@atlaskit/css';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import type { TriggerProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled/box';

import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	createHookWithAssistanceService,
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { Agent } from '@atlassian/conversation-assistant-service/types';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

import type { AgentIconType } from '@confluence/agent-user-dual-avatar/entry-points/agentIconType.ts';
import { getAgentBrand } from '@confluence/agent-user-dual-avatar/entry-points/getAgentBrand.ts';
import {
	expValEquals,
	UNSAFE_isExperimentEnabledNoExposure,
} from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';

import { fetchPresenceAGGUsers } from '../page-presence/fetchPresenceAGGUsers.ts';

import { AgentPresenceMenu } from './AgentPresenceMenu.tsx';
import { AgentPresenceAvatarGroup } from './AgentPresenceAvatarGroup.tsx';
import { getAgentPresenceKey, type AgentWithPresence } from './AgentPresenceTypes.ts';
import {
	buildAgentPresenceAssistanceServiceParams,
	useAgentPresenceResolverMessages,
	useAgentsByIdsWithAgentEdits as useSharedAgentsByIdsWithAgentEdits,
} from './agentPresenceResolver.ts';
import { getAgentPresenceStatus, getNextAgentPresenceBoundary } from './agentPresenceStatus.ts';

export type AgentPresenceProps = {
	isNestedInPresenceAvatarGroup?: boolean;
	onActiveAgentsChange?: (agents: AgentWithPresence[]) => void;
	/**
	 * Presence-only provider for the view route, injected by `PresenceAvatarGroup`. Non-null only
	 * when the page presence connection exists and the agent-view gate is on, so its presence is
	 * what identifies view mode here — no `contentMode` prop is needed.
	 */
	presenceProvider?: Provider | null;
};

type AgentPresenceMetadata = Pick<
	AgentWithPresence,
	| 'actingUserId'
	| 'agentType'
	| 'permit'
	| 'presenceActivity'
	| 'presenceId'
	| 'lastActive'
	| 'presenceStatus'
>;

type ActiveAgentPresenceMetadata = AgentPresenceMetadata & {
	agentId: string;
};

const hasValidLastActive = (lastActive: number | undefined): lastActive is number =>
	typeof lastActive === 'number' && Number.isFinite(lastActive);

const isMoreRecentPresence = (
	current: Pick<AgentWithPresence, 'lastActive'>,
	previous: Pick<AgentWithPresence, 'lastActive'>,
): boolean =>
	hasValidLastActive(current.lastActive) &&
	(!hasValidLastActive(previous.lastActive) || current.lastActive > previous.lastActive);

const i18n = defineMessages({
	agentGroupLabel: {
		id: 'team-presence.agent-group.label',
		defaultMessage: 'Active agents on content',
		description: 'Accessible region landmark for agent presence avatar group',
	},
	externalAgentName: {
		id: 'team-presence.external-agent.name',
		defaultMessage: 'External agent',
		description: 'Display name for an external agent whose details could not be resolved',
	},
	rovoAgentName: {
		id: 'team-presence.rovo-agent.name',
		defaultMessage: 'Rovo',
		description: 'Display name for the Rovo agent in presence labels',
	},
	claudeAgentName: {
		id: 'team-presence.claude-agent.name',
		defaultMessage: 'Claude',
		description: 'Display name for the Claude agent in presence labels',
	},
	cursorAgentName: {
		id: 'team-presence.cursor-agent.name',
		defaultMessage: 'Cursor',
		description: 'Display name for the Cursor agent in presence labels',
	},
	codexAgentName: {
		id: 'team-presence.codex-agent.name',
		defaultMessage: 'Codex',
		description: 'Display name for the Codex agent in presence labels',
	},
	chatgptAgentName: {
		id: 'team-presence.chatgpt-agent.name',
		defaultMessage: 'ChatGPT',
		description: 'Display name for the ChatGPT agent in presence labels',
	},
	diaAgentName: {
		id: 'team-presence.dia-agent.name',
		defaultMessage: 'Dia',
		description: 'Display name for the Dia agent in presence labels',
	},
	figmaAgentName: {
		id: 'team-presence.figma-agent.name',
		defaultMessage: 'Figma',
		description: 'Display name for the Figma agent in presence labels',
	},
	lovableAgentName: {
		id: 'team-presence.lovable-agent.name',
		defaultMessage: 'Lovable',
		description: 'Display name for the Lovable agent in presence labels',
	},
	replitAgentName: {
		id: 'team-presence.replit-agent.name',
		defaultMessage: 'Replit',
		description: 'Display name for the Replit agent in presence labels',
	},
	grokAgentName: {
		id: 'team-presence.grok-agent.name',
		defaultMessage: 'Grok',
		description: 'Display name for the Grok agent in presence labels',
	},
	githubCopilotAgentName: {
		id: 'team-presence.github-copilot-agent.name',
		defaultMessage: 'GitHub Copilot',
		description: 'Display name for the GitHub Copilot agent in presence labels',
	},
	vscodeAgentName: {
		id: 'team-presence.vscode-agent.name',
		defaultMessage: 'Visual Studio Code',
		description: 'Display name for the Visual Studio Code agent in presence labels',
	},
	openCodeAgentName: {
		id: 'team-presence.open-code-agent.name',
		defaultMessage: 'Open Code',
		description: 'Display name for the Open Code agent in presence labels',
	},
	hermesAgentName: {
		id: 'team-presence.hermes-agent.name',
		defaultMessage: 'Hermes',
		description: 'Display name for the Hermes agent in presence labels',
	},
	kiroAgentName: {
		id: 'team-presence.kiro-agent.name',
		defaultMessage: 'Kiro',
		description: 'Display name for the Kiro agent in presence labels',
	},
});

const styles = cssMap({
	nestedTrigger: {
		display: 'inline-flex',
		alignItems: 'center',
	},
});

const getUniqueAgents = (agents: (AgentWithPresence | undefined)[]) => {
	const uniqueAgents = new Map<string, AgentWithPresence>();

	agents?.forEach((agent) => {
		if (agent) {
			const agentPresenceKey = getAgentPresenceKey({
				...agent,
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				includeActingUser: fg('platform_move_presence_agents'),
			});
			const previousAgent = uniqueAgents.get(agentPresenceKey);
			if (
				!previousAgent ||
				!hasValidLastActive(previousAgent.lastActive) ||
				isMoreRecentPresence(agent, previousAgent)
			) {
				uniqueAgents.set(agentPresenceKey, agent);
			}
		}
	});

	return Array.from(uniqueAgents.values());
};

const getUniqueAgentIds = (agentIds: string[]) => Array.from(new Set(agentIds)).sort();

const getAgentIdFromParticipantUserId = (userId: string) => userId.replace(/^agent:/, '');

const areAgentPresenceMetadataMapsEqual = (
	previousPresenceByKey: Map<string, ActiveAgentPresenceMetadata>,
	nextPresenceByKey: Map<string, ActiveAgentPresenceMetadata>,
) => {
	if (previousPresenceByKey.size !== nextPresenceByKey.size) {
		return false;
	}

	return Array.from(nextPresenceByKey).every(([presenceKey, nextPresence]) => {
		const previousPresence = previousPresenceByKey.get(presenceKey);

		return (
			previousPresence?.agentId === nextPresence.agentId &&
			previousPresence?.actingUserId === nextPresence.actingUserId &&
			previousPresence?.agentType === nextPresence.agentType &&
			previousPresence?.permit?.isPermittedToEdit === nextPresence.permit?.isPermittedToEdit &&
			// Compared as well as `isPermittedToEdit`, so a view-permit transition re-renders. Without
			// this an agent gaining or losing view permission would be invisible on the view route,
			// where it is the only permit it has.
			previousPresence?.permit?.isPermittedToView === nextPresence.permit?.isPermittedToView &&
			previousPresence?.presenceActivity === nextPresence.presenceActivity &&
			previousPresence?.presenceId === nextPresence.presenceId &&
			previousPresence?.lastActive === nextPresence.lastActive &&
			previousPresence?.presenceStatus === nextPresence.presenceStatus
		);
	});
};

const updateCachedAgentsById = (
	previousAgentsById: Map<string, AgentWithPresence>,
	activeAgentIds: string[],
	agentEntries: Iterable<readonly [string, AgentWithPresence]>,
) => {
	const activeAgentIdSet = new Set(activeAgentIds);
	let nextAgentsById: Map<string, AgentWithPresence> | undefined;

	for (const [agentId, agent] of agentEntries) {
		if (activeAgentIdSet.has(agentId) && previousAgentsById.get(agentId) !== agent) {
			nextAgentsById ??= new Map(previousAgentsById);
			nextAgentsById.set(agentId, agent);
		}
	}

	previousAgentsById.forEach((_agent, agentId) => {
		if (!activeAgentIdSet.has(agentId)) {
			nextAgentsById ??= new Map(previousAgentsById);
			nextAgentsById.delete(agentId);
		}
	});

	return nextAgentsById ?? previousAgentsById;
};

type UseAgentsByIdsProps = {
	agentIds: string[];
} & AssistanceServiceInjectedParams;

const useActiveAgentPresence = (
	presenceProvider?: Provider | null,
): {
	activeAgentIds: string[];
	activeAgentPresenceKeys: string[];
	activeAgentPresenceByKey: Map<string, ActiveAgentPresenceMetadata>;
} => {
	const [{ collabEditProvider }] = useNativeCollabState();
	const [activeAgentIds, setActiveAgentIds] = useState<string[]>([]);
	const [activeAgentPresenceKeys, setActiveAgentPresenceKeys] = useState<string[]>([]);
	const [activeAgentPresenceByKey, setActiveAgentPresenceByKey] = useState(
		() => new Map<string, ActiveAgentPresenceMetadata>(),
	);
	const lifecycleTimeoutRef = useRef<number | undefined>(undefined);

	// The presence-only provider wins when it exists. It is created for the viewed page, whereas the
	// native-collab store is not scoped by contentId, so an edit provider left over from another
	// content item could otherwise supply the agent list on a published page.
	const provider = presenceProvider ?? collabEditProvider;

	useEffect(() => {
		const clearLifecycleTimeout = () => {
			if (lifecycleTimeoutRef.current !== undefined) {
				window.clearTimeout(lifecycleTimeoutRef.current);
				lifecycleTimeoutRef.current = undefined;
			}
		};

		const updateActiveAgentIds = (agentIds: string[]) => {
			setActiveAgentIds((prev) => {
				const next = getUniqueAgentIds(agentIds);
				return prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next;
			});
		};

		const updateActiveAgentPresenceKeys = (presenceKeys: string[]) => {
			setActiveAgentPresenceKeys((prev) => {
				const next = getUniqueAgentIds(presenceKeys);
				return prev.length === next.length && prev.every((key, i) => key === next[i]) ? prev : next;
			});
		};

		// Only agents that may actually be shown. An agent permitted neither to view nor edit is not
		// rendered — but that filter applies solely to the presence-only provider, because
		// `buildAIProviderPresencePayload` in collab-provider zeroes every permit key for agents
		// upserted locally on the editor route. Filtering those would hide agents that render today.
		const getRenderableAIProviderParticipants = () => {
			const aiProviderParticipants = provider?.getAIProviderParticipants() ?? [];

			if (!presenceProvider) {
				return aiProviderParticipants;
			}

			return aiProviderParticipants.filter(
				(agent) => agent.permit?.isPermittedToView || agent.permit?.isPermittedToEdit,
			);
		};

		const handleUpdatePresenceOld = () => {
			const aiProviderParticipants = getRenderableAIProviderParticipants();
			const agentIds = aiProviderParticipants.map((agent) =>
				getAgentIdFromParticipantUserId(agent.userId),
			);
			const agentPresenceByKey = new Map<string, ActiveAgentPresenceMetadata>();

			aiProviderParticipants.forEach((agent) => {
				const agentId = getAgentIdFromParticipantUserId(agent.userId);
				agentPresenceByKey.set(
					getAgentPresenceKey({
						id: agentId,
						actingUserId: undefined,
						includeActingUser: false,
					}),
					{
						agentId,
						agentType: agent.agentType,
					},
				);
			});

			updateActiveAgentIds(agentIds);
			updateActiveAgentPresenceKeys(Array.from(agentPresenceByKey.keys()));
			setActiveAgentPresenceByKey((previous) =>
				areAgentPresenceMetadataMapsEqual(previous, agentPresenceByKey)
					? previous
					: agentPresenceByKey,
			);
		};

		const handleUpdatePresenceNew = () => {
			clearLifecycleTimeout();
			const now = Date.now();
			const aiProviderParticipants = getRenderableAIProviderParticipants().filter(
				(agent) => getAgentPresenceStatus(agent, now) !== 'expired',
			);
			const agentPresenceByKey = new Map<string, ActiveAgentPresenceMetadata>();
			let nextLifecycleBoundary: number | undefined;

			aiProviderParticipants.forEach((agent) => {
				const agentId = getAgentIdFromParticipantUserId(agent.userId);
				const presenceKey = getAgentPresenceKey({
					id: agentId,
					actingUserId: agent.actingUserId,
					includeActingUser: true,
				});
				const lifecycleBoundary = getNextAgentPresenceBoundary(agent.lastActive, now);
				if (
					lifecycleBoundary !== undefined &&
					(nextLifecycleBoundary === undefined || lifecycleBoundary < nextLifecycleBoundary)
				) {
					nextLifecycleBoundary = lifecycleBoundary;
				}
				const agentPresence = {
					agentId,
					actingUserId: agent.actingUserId,
					agentType: agent.agentType,
					permit: {
						isPermittedToEdit: agent.permit?.isPermittedToEdit,
						isPermittedToView: agent.permit?.isPermittedToView,
					},
					presenceActivity: agent.presenceActivity,
					presenceId: agent.presenceId ?? agent.userId,
					lastActive: agent.lastActive,
					presenceStatus: getAgentPresenceStatus(agent, now),
				} satisfies ActiveAgentPresenceMetadata;
				const previousPresence = agentPresenceByKey.get(presenceKey);
				if (!previousPresence || isMoreRecentPresence(agentPresence, previousPresence)) {
					agentPresenceByKey.set(presenceKey, agentPresence);
				}
			});

			updateActiveAgentIds(Array.from(agentPresenceByKey.values()).map(({ agentId }) => agentId));
			updateActiveAgentPresenceKeys(Array.from(agentPresenceByKey.keys()));
			setActiveAgentPresenceByKey((prev) =>
				areAgentPresenceMetadataMapsEqual(prev, agentPresenceByKey) ? prev : agentPresenceByKey,
			);

			if (nextLifecycleBoundary !== undefined) {
				lifecycleTimeoutRef.current = window.setTimeout(
					handleUpdatePresence,
					Math.max(0, nextLifecycleBoundary - now),
				);
			}
		};

		const handleUpdatePresence = () => {
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (fg('platform_move_presence_agents')) {
				handleUpdatePresenceNew();
				return;
			}

			handleUpdatePresenceOld();
		};

		handleUpdatePresence();

		provider?.on('presence', handleUpdatePresence);
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (fg('platform_move_presence_agents')) {
			provider?.on('presence:changed', handleUpdatePresence);
		}

		return () => {
			clearLifecycleTimeout();
			provider?.off('presence', handleUpdatePresence);
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (fg('platform_move_presence_agents')) {
				provider?.off('presence:changed', handleUpdatePresence);
			}
		};
	}, [presenceProvider, provider]);

	return { activeAgentIds, activeAgentPresenceKeys, activeAgentPresenceByKey };
};

const buildAssistanceServiceParams = (cloudId: string) => ({
	siteId: cloudId,
	product: 'confluence' as const,
	experienceId: 'ai-mate' as const,
	touchpointSource: 'aiAgentPresence' as const,
});

const AgentPresenceView = ({
	activeAgents,
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
}: {
	activeAgents: AgentWithPresence[];
	isNestedInPresenceAvatarGroup?: boolean;
	onActiveAgentsChange?: (agents: AgentWithPresence[]) => void;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isAgentGroupOverflowOpen, setIsAgentGroupOverflowOpen] = useState(false);

	useEffect(() => {
		if (
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			!fg('platform_move_presence_agents') ||
			!isNestedInPresenceAvatarGroup ||
			!onActiveAgentsChange
		) {
			return;
		}

		onActiveAgentsChange(activeAgents);

		return () => {
			onActiveAgentsChange([]);
		};
	}, [activeAgents, isNestedInPresenceAvatarGroup, onActiveAgentsChange]);

	const handleAgentGroupOverflowMenuClick = useCallback(
		(agentCount: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'aiAgentPresence',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'aiAgentPresenceGroupOverflow',
					product: 'confluence',
					attributes: {
						agents: agentCount,
					},
				},
			}).fire();

			setIsAgentGroupOverflowOpen(
				(isAgentGroupOverflowOpenState) => !isAgentGroupOverflowOpenState,
			);
		},
		[createAnalyticsEvent, setIsAgentGroupOverflowOpen],
	);

	const handleAgentGroupOverflowMenuClose = useCallback(() => {
		setIsAgentGroupOverflowOpen(false);
	}, [setIsAgentGroupOverflowOpen]);

	const renderAgentGroupOverflowMenu = useCallback(() => {
		return <AgentPresenceMenu agents={activeAgents} />;
	}, [activeAgents]);

	useEffect(() => {
		if (!activeAgents.length) {
			setIsAgentGroupOverflowOpen(false);
		}
	}, [activeAgents.length]);

	if (!activeAgents?.length || isNestedInPresenceAvatarGroup) {
		return null;
	}

	return (
		<Popup
			placement="bottom-end"
			isOpen={isAgentGroupOverflowOpen}
			onClose={handleAgentGroupOverflowMenuClose}
			content={renderAgentGroupOverflowMenu}
			trigger={(triggerProps: TriggerProps) => (
				<Box
					{...triggerProps}
					paddingInline="space.025"
					xcss={isNestedInPresenceAvatarGroup && styles.nestedTrigger}
					aria-label={
						isNestedInPresenceAvatarGroup ? undefined : formatMessage(i18n.agentGroupLabel)
					}
					role={isNestedInPresenceAvatarGroup ? undefined : 'region'}
				>
					<AgentPresenceAvatarGroup
						agents={activeAgents}
						isOpen={isAgentGroupOverflowOpen}
						onMoreClick={() => handleAgentGroupOverflowMenuClick(activeAgents.length)}
					/>
				</Box>
			)}
		/>
	);
};

const useAgentsByIds: (
	props: Omit<UseAgentsByIdsProps, 'assistanceServiceInjectedParams'> & AssistanceServiceInputParam,
) => Agent[] = createHookWithAssistanceService(
	({ agentIds, assistanceServiceInjectedParams: { assistanceService } }: UseAgentsByIdsProps) => {
		const { data } = useQuery({
			queryKey: ['agentPresenceAgentsByIds', ...agentIds.slice().sort()],
			queryFn: async () => {
				const agentResults = await Promise.allSettled(
					agentIds.map((agentId) => assistanceService.getAgentDetails(agentId)),
				);

				return agentResults.flatMap((result) =>
					result.status === 'fulfilled' ? [result.value] : [],
				);
			},
			enabled: agentIds.length > 0,
		});

		return data ?? [];
	},
);

export const AgentPresenceComponent = ({
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
	presenceProvider,
}: AgentPresenceProps): React.JSX.Element | null => {
	const { cloudId } = useSessionData();
	const { activeAgentIds, activeAgentPresenceKeys, activeAgentPresenceByKey } =
		useActiveAgentPresence(presenceProvider);

	const agentsFromAssistanceService = useAgentsByIds({
		agentIds: activeAgentIds,
		assistanceServiceParams: buildAssistanceServiceParams(cloudId),
	});

	const [agentsById, setAgentsById] = useState(() => new Map<string, AgentWithPresence>());

	useEffect(() => {
		setAgentsById((previousAgentsById) => {
			return updateCachedAgentsById(
				previousAgentsById,
				activeAgentIds,
				agentsFromAssistanceService.map((agent) => [agent.id, agent] as const),
			);
		});
	}, [activeAgentIds, agentsFromAssistanceService]);

	const activeAgents = useMemo(() => {
		return getUniqueAgents(
			activeAgentPresenceKeys.map((presenceKey) => {
				const presence = activeAgentPresenceByKey.get(presenceKey);
				const agentId = presence?.agentId;
				const agent =
					agentId !== undefined
						? (agentsFromAssistanceService.find((agent) => agent.id === agentId) ??
							agentsById.get(agentId))
						: undefined;

				return agent
					? {
							...agent,
							...presence,
						}
					: agent;
			}),
		);
	}, [activeAgentPresenceByKey, activeAgentPresenceKeys, agentsById, agentsFromAssistanceService]);

	return (
		<AgentPresenceView
			activeAgents={activeAgents}
			isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
			onActiveAgentsChange={onActiveAgentsChange}
		/>
	);
};

// Keep the gate-off streaming cohorts on the existing resolver so the shared page/editor path has
// an independent rollback switch.
const createFallbackAgent = (agentId: string, name: string): AgentWithPresence => ({
	id: agentId,
	identity_account_id: null,
	named_id: agentId,
	name,
	description: null,
	creator_type: 'CUSTOMER',
	available_plugins: {},
	is_default: false,
	actor_type: 'AGENT',
	user_defined_conversation_starters: null,
	favourite: false,
	deactivated: false,
	isExternalAgentFallback: true,
});

type AgentIdentifier = {
	agentId: string;
	agentType?: string;
};

type UseAgentIdentifiersWithAgentEditsProps = {
	agentIdentifiers: AgentIdentifier[];
	fallbackAgentName: string;
	agentNames: Record<
		| 'chatgpt'
		| 'claude'
		| 'codex'
		| 'cursor'
		| 'dia'
		| 'figma'
		| 'github_copilot'
		| 'grok'
		| 'hermes'
		| 'kiro'
		| 'lovable'
		| 'open_code'
		| 'replit'
		| 'rovo'
		| 'vscode',
		string
	>;
} & AssistanceServiceInjectedParams;

type AssistanceService =
	AssistanceServiceInjectedParams['assistanceServiceInjectedParams']['assistanceService'];

type IdentityUser = Awaited<ReturnType<typeof fetchPresenceAGGUsers>>[number];

const hasConcreteAgentId = ({ agentId, agentType }: AgentIdentifier): boolean =>
	Boolean(agentId) && agentId !== agentType;

const createIdentityAgent = (
	identifier: AgentIdentifier,
	user: IdentityUser,
	fallbackAgentName: string,
): AgentWithPresence => ({
	...createFallbackAgent(identifier.agentId, fallbackAgentName),
	agentType: identifier.agentType,
	avatarUrl: user.avatar,
	id: identifier.agentId,
	identity_account_id: user.userId,
	isExternalAgentFallback: false,
	named_id: user.userId,
	name: user.name,
});

const createTypedFallbackAgent = (
	identifier: AgentIdentifier,
	fallbackAgentName: string,
	agentIcon?: AgentIconType,
): AgentWithPresence => ({
	...createFallbackAgent(identifier.agentId, fallbackAgentName),
	agentType: identifier.agentType,
	...(agentIcon ? { agentIcon } : {}),
});

const createRovoChatAgent = (identifier: AgentIdentifier, name: string): AgentWithPresence =>
	createTypedFallbackAgent({ ...identifier, agentId: 'rovo_chat' }, name, 'rovo');

const resolveStudioAgent = async (
	identifier: AgentIdentifier,
	assistanceService: AssistanceService,
	fallbackAgentName: string,
	rovoAgentName: string,
): Promise<AgentWithPresence> => {
	if (!hasConcreteAgentId(identifier)) {
		return createRovoChatAgent(identifier, rovoAgentName);
	}

	try {
		const agent = await assistanceService.getAgentDetailsByIdentityAccountId(identifier.agentId);

		if (
			isDefaultRovoChat({
				agentId: agent.id,
				agentNamedId: agent.named_id,
			})
		) {
			return createRovoChatAgent(identifier, rovoAgentName);
		}

		const agentBrand =
			agent.creator_type === 'OOTB' ? getAgentBrand(agent.name.replace(/ Agent$/, '')) : undefined;

		return {
			...agent,
			agentType: identifier.agentType,
			...(agentBrand && { agentIcon: agentBrand.agentIcon }),
		};
	} catch {
		return createTypedFallbackAgent(identifier, fallbackAgentName, 'ai-agent');
	}
};

const resolveStudioAgentById = async (
	identifier: AgentIdentifier,
	assistanceService: AssistanceService,
	fallbackAgentName: string,
): Promise<AgentWithPresence> => {
	try {
		return {
			...(await assistanceService.getAgentDetails(identifier.agentId)),
			agentType: identifier.agentType,
		};
	} catch {
		return createTypedFallbackAgent(identifier, fallbackAgentName, 'ai-agent');
	}
};

const resolveIdentityAgent = async (
	identifier: AgentIdentifier,
	identityUsersByIdPromise: Promise<Map<string, IdentityUser>>,
	fallbackAgentName: string,
): Promise<AgentWithPresence> => {
	const user = (await identityUsersByIdPromise).get(identifier.agentId);

	return user
		? createIdentityAgent(identifier, user, fallbackAgentName)
		: createTypedFallbackAgent(identifier, fallbackAgentName, 'ai-agent');
};

const resolveAgentIdentifier = (
	identifier: AgentIdentifier,
	assistanceService: AssistanceService,
	identityUsersByIdPromise: Promise<Map<string, IdentityUser>>,
	fallbackAgentName: string,
	agentNames: UseAgentIdentifiersWithAgentEditsProps['agentNames'],
): Promise<AgentWithPresence> | AgentWithPresence => {
	switch (identifier.agentType) {
		case 'claude':
			return createTypedFallbackAgent(identifier, agentNames.claude, 'claude');
		case 'codex':
			return createTypedFallbackAgent(identifier, agentNames.codex, 'codex');
		case 'cursor':
			return createTypedFallbackAgent(identifier, agentNames.cursor, 'cursor');
		case 'chatgpt':
			return createTypedFallbackAgent(identifier, agentNames.chatgpt, 'chatgpt');
		case 'dia':
			return createTypedFallbackAgent(identifier, agentNames.dia, 'dia');
		case 'figma':
			return createTypedFallbackAgent(identifier, agentNames.figma, 'figma');
		case 'lovable':
			return createTypedFallbackAgent(identifier, agentNames.lovable, 'lovable');
		case 'replit':
			return createTypedFallbackAgent(identifier, agentNames.replit, 'replit');
		case 'grok':
			return createTypedFallbackAgent(identifier, agentNames.grok, 'grok');
		case 'github_copilot':
			return createTypedFallbackAgent(identifier, agentNames.github_copilot, 'github-copilot');
		case 'vscode':
			return createTypedFallbackAgent(identifier, agentNames.vscode, 'vscode');
		case 'open_code':
			return createTypedFallbackAgent(identifier, agentNames.open_code, 'open-code');
		case 'hermes':
			return createTypedFallbackAgent(identifier, agentNames.hermes, 'hermes');
		case 'kiro':
			return createTypedFallbackAgent(identifier, agentNames.kiro, 'kiro');
		case 'convo-ai':
			return resolveStudioAgent(identifier, assistanceService, fallbackAgentName, agentNames.rovo);
		case 'mcp':
			return resolveIdentityAgent(identifier, identityUsersByIdPromise, fallbackAgentName);
		case 'twg':
			return createTypedFallbackAgent(identifier, fallbackAgentName, 'ai-agent');
		case 'rovo_chat':
			return createRovoChatAgent(identifier, agentNames.rovo);
		default:
			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (!fg('platform_move_presence_agents') && identifier.agentType === undefined) {
				return resolveStudioAgentById(identifier, assistanceService, fallbackAgentName);
			}

			return createTypedFallbackAgent(identifier, fallbackAgentName, 'ai-agent');
	}
};

const useAgentsByIdsWithAgentEdits: (
	props: Omit<UseAgentIdentifiersWithAgentEditsProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => Map<string, AgentWithPresence> | null = createHookWithAssistanceService(
	({
		agentIdentifiers,
		fallbackAgentName,
		agentNames,
		assistanceServiceInjectedParams: { assistanceService },
	}: UseAgentIdentifiersWithAgentEditsProps) => {
		const { data } = useQuery({
			queryKey: [
				'agentPresenceAgentsByIds',
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				fg('confluence_agent_presence_profile_by_type') ? 'type-directed' : 'legacy',
				...agentIdentifiers.map(({ agentId, agentType }) => `${agentType ?? ''}:${agentId}`).sort(),
			],
			queryFn: async () => {
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				if (!fg('confluence_agent_presence_profile_by_type')) {
					const entries = await Promise.all(
						agentIdentifiers.map(async (identifier): Promise<[string, AgentWithPresence]> => {
							const { agentId } = identifier;
							try {
								return [agentId, await assistanceService.getAgentDetails(agentId)];
							} catch {
								try {
									return [
										agentId,
										await assistanceService.getAgentDetailsByIdentityAccountId(agentId),
									];
								} catch {
									return [
										agentId,
										createTypedFallbackAgent(identifier, fallbackAgentName, 'ai-agent'),
									];
								}
							}
						}),
					);

					return new Map(entries);
				}

				const identityAgentIds = agentIdentifiers.flatMap((identifier) =>
					hasConcreteAgentId(identifier) && identifier.agentType === 'mcp'
						? [identifier.agentId]
						: [],
				);
				const identityUsersByIdPromise: Promise<Map<string, IdentityUser>> =
					identityAgentIds.length > 0
						? fetchPresenceAGGUsers(identityAgentIds)
								.then((users) => new Map(users.map((user) => [user.userId, user])))
								.catch(() => new Map())
						: Promise.resolve(new Map());

				return new Map(
					await Promise.all(
						agentIdentifiers.map(
							async (identifier) =>
								[
									identifier.agentId,
									await resolveAgentIdentifier(
										identifier,
										assistanceService,
										identityUsersByIdPromise,
										fallbackAgentName,
										agentNames,
									),
								] as const,
						),
					),
				);
			},
			enabled: agentIdentifiers.length > 0,
		});

		return data ?? null;
	},
);

const AgentPresenceComponentWithAgentEdits = ({
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
	presenceProvider,
}: AgentPresenceProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { cloudId } = useSessionData();
	const { activeAgentIds, activeAgentPresenceKeys, activeAgentPresenceByKey } =
		useActiveAgentPresence(presenceProvider);
	const activeAgentTypeById = useMemo(() => {
		const agentTypeById = new Map<string, AgentPresenceMetadata['agentType']>();

		activeAgentPresenceKeys.forEach((presenceKey) => {
			const presence = activeAgentPresenceByKey.get(presenceKey);

			if (presence && !agentTypeById.has(presence.agentId)) {
				agentTypeById.set(presence.agentId, presence.agentType);
			}
		});

		return agentTypeById;
	}, [activeAgentPresenceByKey, activeAgentPresenceKeys]);
	const activeAgentIdentifiers = useMemo(
		() =>
			activeAgentIds.map((agentId) => ({
				agentId,
				agentType: activeAgentTypeById.get(agentId),
			})),
		[activeAgentIds, activeAgentTypeById],
	);

	const resolvedAgentsById = useAgentsByIdsWithAgentEdits({
		agentIdentifiers: activeAgentIdentifiers,
		fallbackAgentName: formatMessage(i18n.externalAgentName),
		agentNames: {
			chatgpt: formatMessage(i18n.chatgptAgentName),
			claude: formatMessage(i18n.claudeAgentName),
			codex: formatMessage(i18n.codexAgentName),
			cursor: formatMessage(i18n.cursorAgentName),
			dia: formatMessage(i18n.diaAgentName),
			figma: formatMessage(i18n.figmaAgentName),
			github_copilot: formatMessage(i18n.githubCopilotAgentName),
			grok: formatMessage(i18n.grokAgentName),
			hermes: formatMessage(i18n.hermesAgentName),
			kiro: formatMessage(i18n.kiroAgentName),
			lovable: formatMessage(i18n.lovableAgentName),
			open_code: formatMessage(i18n.openCodeAgentName),
			replit: formatMessage(i18n.replitAgentName),
			rovo: formatMessage(i18n.rovoAgentName),
			vscode: formatMessage(i18n.vscodeAgentName),
		},
		assistanceServiceParams: buildAssistanceServiceParams(cloudId),
	});

	return (
		<ResolvedAgentPresenceComponent
			activeAgentIds={activeAgentIds}
			activeAgentPresenceKeys={activeAgentPresenceKeys}
			activeAgentPresenceByKey={activeAgentPresenceByKey}
			resolvedAgentsById={resolvedAgentsById}
			isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
			onActiveAgentsChange={onActiveAgentsChange}
		/>
	);
};

const SharedAgentPresenceComponentWithAgentEdits = ({
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
	presenceProvider,
}: AgentPresenceProps): React.JSX.Element | null => {
	const { cloudId } = useSessionData();
	const { agentNames, fallbackAgentName } = useAgentPresenceResolverMessages();
	const { activeAgentIds, activeAgentPresenceKeys, activeAgentPresenceByKey } =
		useActiveAgentPresence(presenceProvider);
	const activeAgentTypeById = useMemo(() => {
		const agentTypeById = new Map<string, AgentPresenceMetadata['agentType']>();

		activeAgentPresenceKeys.forEach((presenceKey) => {
			const presence = activeAgentPresenceByKey.get(presenceKey);

			if (presence && !agentTypeById.has(presence.agentId)) {
				agentTypeById.set(presence.agentId, presence.agentType);
			}
		});

		return agentTypeById;
	}, [activeAgentPresenceByKey, activeAgentPresenceKeys]);
	const activeAgentIdentifiers = useMemo(
		() =>
			activeAgentIds.map((agentId) => ({
				agentId,
				agentType: activeAgentTypeById.get(agentId),
			})),
		[activeAgentIds, activeAgentTypeById],
	);
	const resolvedAgentsById = useSharedAgentsByIdsWithAgentEdits({
		agentIdentifiers: activeAgentIdentifiers,
		fallbackAgentName,
		agentNames,
		assistanceServiceParams: buildAgentPresenceAssistanceServiceParams(cloudId),
	});

	return (
		<ResolvedAgentPresenceComponent
			activeAgentIds={activeAgentIds}
			activeAgentPresenceKeys={activeAgentPresenceKeys}
			activeAgentPresenceByKey={activeAgentPresenceByKey}
			resolvedAgentsById={resolvedAgentsById}
			isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
			onActiveAgentsChange={onActiveAgentsChange}
		/>
	);
};

function ResolvedAgentPresenceComponent({
	activeAgentIds,
	activeAgentPresenceKeys,
	activeAgentPresenceByKey,
	resolvedAgentsById,
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
}: Pick<AgentPresenceProps, 'isNestedInPresenceAvatarGroup' | 'onActiveAgentsChange'> & {
	activeAgentIds: string[];
	activeAgentPresenceKeys: string[];
	activeAgentPresenceByKey: Map<string, ActiveAgentPresenceMetadata>;
	resolvedAgentsById: Map<string, AgentWithPresence> | null;
}): React.JSX.Element | null {
	const [agentsById, setAgentsById] = useState(() => new Map<string, AgentWithPresence>());

	useEffect(() => {
		setAgentsById((previous) => {
			return updateCachedAgentsById(previous, activeAgentIds, resolvedAgentsById ?? []);
		});
	}, [activeAgentIds, resolvedAgentsById]);

	const activeAgents = useMemo(
		() =>
			getUniqueAgents(
				activeAgentPresenceKeys.map((presenceKey) => {
					const presence = activeAgentPresenceByKey.get(presenceKey);
					const agentId = presence?.agentId;
					const resolvedAgent = agentId ? resolvedAgentsById?.get(agentId) : undefined;
					const cachedAgent = agentId ? agentsById.get(agentId) : undefined;
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					const agent = fg('confluence_agent_presence_profile_by_type')
						? ((resolvedAgent?.agentType === presence?.agentType ? resolvedAgent : undefined) ??
							(cachedAgent?.agentType === presence?.agentType ? cachedAgent : undefined))
						: (resolvedAgent ?? cachedAgent);

					return agent
						? {
								...agent,
								...presence,
							}
						: agent;
				}),
			),
		[activeAgentPresenceByKey, activeAgentPresenceKeys, agentsById, resolvedAgentsById],
	);

	return (
		<AgentPresenceView
			activeAgents={activeAgents}
			isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
			onActiveAgentsChange={onActiveAgentsChange}
		/>
	);
}

export const AgentPresence = ({
	isNestedInPresenceAvatarGroup,
	onActiveAgentsChange,
	presenceProvider,
}: AgentPresenceProps): React.JSX.Element | null => {
	const { isAdminHubAIEnabled, isAIEarlyAccessEnabled } = useSessionData();

	if (
		!isAdminHubAIEnabled ||
		!isAIEarlyAccessEnabled ||
		!expValEquals('reinstate_confluence_agent_presence', 'isEnabled', true) ||
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		(isNestedInPresenceAvatarGroup && !fg('platform_move_presence_agents'))
	) {
		return null;
	}

	// Document-editing agents are resolved by type, which is the only path that can present an agent
	// with no concrete id (a Quick Action attributes `convo-ai` without one). Backend/3P agent
	// streaming, the frontend streaming UX milestone, and moved agent presence each enable this path.
	//
	// The milestone value is read without logging an exposure: this component mounts for every
	// eligible session, whereas the canonical exposure is logged by the editor AI plugin when a
	// streaming action runs. The backend experiment is evaluated first so its exposure population is
	// unchanged.
	return (
		<ErrorBoundary attribution={Attribution.COLLABORATION_PRESENCE}>
			{/* eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred. */}
			{fg('confluence_agent_presence_shared_agent_code') ? (
				<SharedAgentPresenceComponentWithAgentEdits
					isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
					onActiveAgentsChange={onActiveAgentsChange}
					presenceProvider={presenceProvider}
				/>
			) : expValEquals('platform_editor_agent_be_streaming', 'isEnabled', true) ||
			  UNSAFE_isExperimentEnabledNoExposure('platform_editor_ai_streaming_ux_experience_m1') ||
			  // eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			  fg('platform_move_presence_agents') ? (
				<AgentPresenceComponentWithAgentEdits
					isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
					onActiveAgentsChange={onActiveAgentsChange}
					presenceProvider={presenceProvider}
				/>
			) : (
				<AgentPresenceComponent
					isNestedInPresenceAvatarGroup={isNestedInPresenceAvatarGroup}
					onActiveAgentsChange={onActiveAgentsChange}
					presenceProvider={presenceProvider}
				/>
			)}
		</ErrorBoundary>
	);
};
